import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';
import Compressor from 'compressorjs';

import { UploadIcon } from 'Lib/components/common/StyledComponents';
import useNotify from 'Lib/hooks/useNotify';
import fileSizeValidator from 'Lib/utilities/fileSizeValidator';
import getIn from 'Lib/utilities/getIn';
import isEmpty from 'Lib/utilities/isEmpty';

import ImageManagerContext from './context/context';

/**
 * Upload component for handling file uploads and displaying file previews
 * @param {ReactNode} children - The content to display within the component
 * @param {boolean} button - Whether to render as a button for file selection
 * @param {function} upload - An endpoint to handle file uploads
 * @param {array} defaultPreviews - The default previews to display
 * @param {string} allowedExtensions - Allowed file extensions for uploads
 * @param {string} fileType - The type of file being uploaded (default is 'file')
 * @param {string} filePath - The path in the response data to retrieve file ID (default is 'fileID')
 * @param {string} placeholder - The text displayed in the dropzone
 * @param {number} maxFiles - The maximum number of files allowed for upload
 * @param {function} onDelete - A callback function for file deletion
 * @param {function} onUpload - A callback function for successful file uploads
 * @param {object} rest - Additional props to spread
 * @param {boolean} disabled - Whether the component is disabled
 */
export default function Upload({
	children,
	button,
	defaultPreviews = [],
	upload,
	allowedExtensions,
	fileType = 'file',
	filePath = 'fileID',
	onDelete,
	onUpload,
	placeholder,
	maxFiles,
	disabled,
	...rest
}) {
	const imageManagerContext = useContext(ImageManagerContext);

	const [uploading, setUploading] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [previews, setPreviews] = useState(defaultPreviews);
	const prevPreviews = useRef(previews);

	useEffect(() => {
		// File(s) have been uploaded
		if (previews > prevPreviews.current && onUpload) {
			onUpload(
				uploadedFiles,
				previews.map(preview => preview.fileID)
			);
			// File(s) have been removed
		} else if (previews < prevPreviews.current && onDelete) {
			const notDeletedFiles = previews.map(preview => preview.fileID);
			onDelete(notDeletedFiles);

			const updatedFileArray = uploadedFiles.filter(file => notDeletedFiles.includes(file.fileID));
			setUploadedFiles(updatedFileArray);
		}

		// Update the previous value to the current value
		prevPreviews.current = previews;
	}, [previews]);

	const getPreviewsFromResponse = (responses, files) => {
		return responses.map((response, index) => ({
			label: Array.isArray(files) ? files[index].name : files.name,
			fileID: getIn(response.data, filePath)
		}));
	};

	const toggleUploading = state => {
		if (imageManagerContext) {
			const { setUploading: setContextUploading } = imageManagerContext;

			setContextUploading(state);
		} else {
			setUploading(state);
		}
	};

	const handleUpload = files => {
		toggleUploading(true);

		// If endpoint is provided, create the array of promises here
		if (upload) {
			const uploads = Array.isArray(files) ? files.map(file => upload(file)) : [upload(files)];

			Promise.all(uploads)
				.then(responses => {
					const previews = getPreviewsFromResponse(responses, files);

					setPreviews(prevState => prevState.concat(previews));

					// Add corresponding preview fileID to each file uploaded
					const newFiles = Array.isArray(files) ? files.map(file => file) : [files];
					newFiles.forEach(function (file, index) {
						file.fileID = previews[index].fileID;
					});

					setUploadedFiles(prevState => prevState.concat(newFiles));
				})
				.catch(toastError)
				.finally(() => {
					toggleUploading(false);
				});
		} else {
			// Otherwise, let the parent component control the upload process (SingleUnit/MultiUnit)
			onUpload(files)
				.then(response => {
					// If the parent components upload function returns undefined, don't add a previews
					if (response === undefined) return;

					const previews = getPreviewsFromResponse(Array.isArray(response) ? response : [response], files);

					setPreviews(prevState => prevState.concat(previews));
				})
				.finally(() => {
					toggleUploading(false);
				});
		}
	};

	const handleFileDelete = file => {
		const newPreviews = previews.filter(preview => preview.fileID != file.fileID);

		if (onDelete) {
			onDelete(newPreviews);
		}
		setPreviews(newPreviews);
	};

	const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
		const filesToUpload = [];

		const uploadFiles = file => {
			if (acceptedFiles.length < 2) {
				handleUpload(file);
			} else {
				filesToUpload.push(file);
			}
		};

		for (const file of acceptedFiles) {
			if (file.size > 3000000) {
				toggleUploading(true);

				const compressedFile = await compressFile(file);
				uploadFiles(compressedFile);
			} else {
				uploadFiles(file);
			}
		}

		if (filesToUpload.length > 0) {
			handleUpload(filesToUpload);
		}

		let tooManyFilesErrorShown = false;

		for (const { errors } of rejectedFiles) {
			for (const error of errors) {
				if (error.code === 'too-many-files' && !tooManyFilesErrorShown) {
					toastError(`You can only upload ${maxFiles} file${maxFiles > 1 ? 's' : ''}.`);
					tooManyFilesErrorShown = true;
				} else if (error.code !== 'too-many-files') toastError(error.message);
			}
		}
	}, []);

	const compressFile = async file => {
		return new Promise(resolve => {
			new Compressor(file, {
				quality: 0.6,
				maxWidth: 2000,
				maxHeight: 2000,
				name: file.name,
				mimeType: 'image/jpeg',
				convertSize: 3000000,
				success: compressedBlob => {
					const compressedFile = new File(
						[compressedBlob],
						file.name, // keep the original filename
						{
							type: compressedBlob.type,
							lastModified: Date.now()
						}
					);
					resolve(compressedFile);
				},
				error: () => {
					toastError('File is too large to be uploaded. Maximum upload size is 40 MB');
					toggleUploading(false);
					resolve(); // Resolve with nothing on error
				}
			});
		});
	};

	const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
		onDrop,
		accept: allowedExtensions,
		validator: fileSizeValidator,
		maxFiles: maxFiles
	});

	const { toastError } = useNotify();

	return button ? (
		<Button {...getRootProps()} {...rest} onClick={open}>
			<input {...getInputProps()} />
			{children}
		</Button>
	) : (
		<>
			<DropzoneInput sx={disabled ? { pointerEvents: 'none', opacity: '0.4' } : {}} {...getRootProps()} {...rest}>
				<input {...getInputProps()} />
				{children ? (
					children
				) : (
					<Stack>
						<UploadContainer uploading={uploading}>
							{uploading ? (
								<CircularProgress />
							) : (
								<Box>
									{!placeholder ? (
										<>
											<UploadIcon sx={{ mx: 'auto' }}>upload_file</UploadIcon>
											{isDragActive ? (
												<Typography>Drop file(s) here</Typography>
											) : (
												<Typography>
													Select a {fileType} to upload
													<br />
													or drag and drop it here.
												</Typography>
											)}
										</>
									) : (
										<Typography>{placeholder}</Typography>
									)}
								</Box>
							)}
						</UploadContainer>
					</Stack>
				)}
			</DropzoneInput>
			{!isEmpty(previews) &&
				previews.map((preview, index) => (
					<Box
						key={index}
						alt={`${preview.label}-image-preview`}
						sx={{
							border: '1px solid',
							borderColor: 'common.lightgray',
							borderRadius: '12px',
							display: 'flex',
							justifyContent: 'space-between',
							mt: 4
						}}
						{...rest.sx}>
						<Typography
							sx={{
								alignSelf: 'center',
								pl: 4,
								fontSize: theme => theme.typography.pxToRem(16)
							}}>
							{preview.label}
						</Typography>
						<IconButton onClick={() => handleFileDelete(preview, index)} disabled={preview.deleting}>
							{preview.deleting ? <CircularProgress size={20} /> : 'delete'}
						</IconButton>
					</Box>
				))}
		</>
	);
}

const UploadContainer = styled(Box, { shouldForwardProp: prop => prop !== 'uploading' })(({ uploading }) => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexDirection: 'column',
	height: uploading ? 120 : 'inherit'
}));

const DropzoneInput = styled(Box, { shouldForwardProp: prop => prop !== 'fillContainer' })(({ theme, fillContainer }) => ({
	backgroundColor: theme.palette.common.lightblue,
	border: `1px dashed ${theme.palette.common.blue}`,
	borderRadius: 12,
	textAlign: 'center',
	padding: 12,
	...(fillContainer && {
		height: '100%',
		width: '100%'
	}),
	'&:hover': {
		cursor: 'pointer',
		backgroundColor: '#e4f3ff'
	},
	p: {
		margin: 0,
		color: theme.palette.common.blue
	}
}));
